/**
 * The single bridge between the worker and React.
 *
 * Everything above this line deals in `Snapshot` and `Action`. Nothing above it can reach
 * `SimState`, which is what keeps the UI honest about the difference between what the instruments
 * say and what is true (DECISIONS D6).
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import type { Action } from '../engine/actions';
import { EngineClient } from '../worker/client';
import type { Speed } from '../worker/protocol';
import type { WhatIfRequest } from '../worker/whatif';
import { useSession } from '../store/session';
import { listSaves, putSave, recordResult } from '../store/persistence';
import { CAMPAIGN } from '../scenarios';
import type { SandboxOptions } from '../scenarios/sandbox';

interface EngineApi {
  dispatch(...actions: Action[]): void;
  start(
    siteId: string,
    scenarioId: string,
    difficulty: 'trainee' | 'operator' | 'chief',
    seed: number,
    sandbox?: SandboxOptions | null,
  ): void;
  setSpeed(s: Speed): void;
  togglePause(): void;
  save(name: string): void;
  whatIf(requests: WhatIfRequest[]): void;
  loadSave(name: string): void;
}

const Ctx = createContext<EngineApi | null>(null);

export function EngineProvider({ children }: { children: ReactNode }): JSX.Element {
  const clientRef = useRef<EngineClient | null>(null);
  /**
   * Commands issued before the worker exists.
   *
   * `useEffect` runs after paint, so there is a small window in which the user can click "Start
   * shift" before the worker is constructed. Optional-chaining the call would drop it silently,
   * which is exactly the class of bug non-negotiable #6 exists to prevent — so queue instead.
   */
  const queued = useRef<((c: EngineClient) => void)[]>([]);
  const s = useSession;

  const withClient = useCallback((fn: (c: EngineClient) => void): void => {
    const c = clientRef.current;
    if (c) fn(c);
    else queued.current.push(fn);
  }, []);

  useEffect(() => {
    // The worker is created *inside* the effect, not during render. StrictMode double-invokes
    // effects in development: creating it during render and terminating it in cleanup left the
    // second run re-attaching handlers to an already-terminated worker, and no snapshot ever
    // arrived. Creating and terminating in the same effect body keeps them paired.
    const client = new EngineClient();
    clientRef.current = client;

    client.on({
      onSnapshot: (snap, secondary) => {
        s.getState().setSnapshots(snap, secondary);
      },
      onEnd: (card, snap, secondaryCard, secondarySnap) => {
        const st = s.getState();
        st.setSnapshots(snap, secondarySnap);
        st.setSecondaryCard(secondaryCard);
        st.setCard(card);
        st.setView('debrief');
        st.setPhase('debrief');
        st.setPaused(true);
        const idx = CAMPAIGN.findIndex((c) => c.id === st.scenarioId);
        const next = idx >= 0 ? CAMPAIGN[idx + 1] : undefined;
        void recordResult(
          {
            scenarioId: st.scenarioId,
            difficulty: st.difficulty,
            passed: card.passed,
            net: card.net,
            grade: card.grade,
            atMs: Date.now(),
          },
          next ? next.id : null,
        );
      },
      onFault: (message) => {
        const st = s.getState();
        st.setFault(message);
        st.setPaused(true);
      },
      onSaved: (save) => {
        void putSave(save).then(async () => {
          s.getState().setSaves(await listSaves());
          s.getState().setToast(`Saved “${save.name}”`);
        });
      },
      onWhatIf: (_id, traces) => {
        const st = s.getState();
        st.setWhatIf(traces);
        st.setWhatIfPending(false);
      },
    });
    // Drain anything the user triggered before the worker existed.
    const pending = queued.current;
    queued.current = [];
    for (const fn of pending) fn(client);

    return () => {
      client.terminate();
      clientRef.current = null;
    };
  }, [s]);

  const api = useMemo<EngineApi>(
    () => ({
      // Actions always go to the console the operator is looking at.
      dispatch: (...actions: Action[]) =>
        withClient((c) => c.dispatch(s.getState().activeSite, ...actions)),
      start: (siteId, scenarioId, difficulty, seed, sandbox = null) => {
        const st = s.getState();
        st.configure({ siteId, scenarioId, difficulty, seed });
        st.setCard(null as never);
        st.setSecondaryCard(null);
        st.setActiveSite('primary');
        st.setFault(null);
        st.setWhatIf(null);
        st.setPhase('running');
        st.setView('control-room');
        st.setPaused(true);
        st.setSpeed(1);
        withClient((c) => c.init(siteId, scenarioId, difficulty, seed, sandbox));
      },
      setSpeed: (speed) => {
        s.getState().setSpeed(speed);
        withClient((c) => c.setSpeed(speed));
      },
      togglePause: () => {
        const st = s.getState();
        const next = !st.paused;
        st.setPaused(next);
        withClient((c) => (next ? c.pause() : c.resume()));
      },
      save: (name) => withClient((c) => c.requestSave(name)),
      whatIf: (requests) => {
        s.getState().setWhatIfPending(true);
        withClient((c) => c.whatIf(requests));
      },
      loadSave: (name) => {
        void listSaves().then((all) => {
          const save = all.find((x) => x.name === name);
          if (!save) return;
          const st = s.getState();
          st.configure({
            siteId: save.siteId,
            scenarioId: save.scenarioId,
            difficulty: save.difficulty as 'trainee' | 'operator' | 'chief',
            seed: save.runLog.seed,
          });
          st.setPhase('running');
          st.setView('control-room');
          st.setPaused(true);
          withClient((c) => c.load(save));
        });
      },
    }),
    [s, withClient],
  );

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useEngine(): EngineApi {
  const api = useContext(Ctx);
  if (!api) throw new Error('useEngine outside EngineProvider');
  return api;
}

/** Dispatch helper that keeps callers from rebuilding closures every render. */
export function useDispatch(): (...actions: Action[]) => void {
  const engine = useEngine();
  return useCallback((...actions: Action[]) => engine.dispatch(...actions), [engine]);
}
